import React, { useEffect, useState, useCallback } from "react";
import { TableHeaderColumn } from "react-bootstrap-table";
import { withRouter } from "react-router-dom";

import EntityTable from "../../component/EntityTable";
import Grouping from "./Grouping";
import { TAKS_IMPORT } from "../../config/lang";

import CreateMsgPopup from "../../util/CreateMsgPopup";
import { networkName, pointState, locality } from "../../util/formatter";
import { translate } from "../../util/util";

const List = ({ currentUser, history }) => {
  const [groupStatusData, setGroupStatusData] = React.useState({ type: "list", ids: [] });
  const [filterKeys, setFilterKeys] = React.useState();
  const [list, setList] = React.useState();
  const [checkedRows, setCheckedRows] = React.useState([]);
  const [valid, setValid] = useState(false);
  const [groupPopup, setGroupPopup] = useState(false);
  const [isMsgStatus, setIsMsgStatus] = React.useState({ status: false, type: "error", message: "" });
  const [wasAllPointsAdded, setWasAllPointsAdded] = useState(false);
  const [clearPointsTrigger, setClearPointsTrigger] = useState(0);
  const [forceUncheckedSelectAll, setForceUncheckedSelectAll] = useState(false);

  useEffect(() => {
    if (currentUser.role === "supervisor_manager" || currentUser.role === "supervisor") {
      setValid(true);
    }
  }, [currentUser.role]);

  useEffect(() => {
    if (groupStatusData.ids) {
      setCheckedRows(
        groupStatusData.ids.map(item => ({
          id: item.id,
          name: item.name,
        }))
      );
    }
  }, [JSON.stringify(groupStatusData.ids), groupStatusData.type]);

  useEffect(() => {
    // If the type becomes "list", we force-clear all selected checkboxes.
    if (groupStatusData.type === "list") {
      setCheckedRows([]);
    }
  }, [groupStatusData.type]);

  useEffect(() => {
    setWasAllPointsAdded(false);
  }, [groupStatusData.type]);

  const getCurrentGroupData = useCallback((type, ids) => {
    setGroupStatusData(prev => {
      //Important: check if the data is really new, so we don't set the same
      if (prev.type === type && JSON.stringify(prev.ids) === JSON.stringify(ids)) {
        return prev;
      }
      return { ...prev, type, ids };
    });
  }, []);

  const handleCheckboxChange = (e, row) => {
    setForceUncheckedSelectAll(false);
    const isChecked = e.target.checked;

    if (isChecked) {
      setCheckedRows(prev => [...prev, { id: row.id, name: row.name }]);
    } else {
      setCheckedRows(prev => prev.filter(item => item.id !== row.id));
    }
  };

  const handleSelectAllOnePage = () => {
    const isAddToExistingMode = forceUncheckedSelectAll && checkedRows.length > 0;
    setForceUncheckedSelectAll(false);

    if (checkedRows.length > 0 && !forceUncheckedSelectAll) {
      setCheckedRows([]);
      return;
    }

    const validRowIds = list.filter(row => row?.id).map(row => ({ id: row.id, name: row.name }));

    if (validRowIds.length === 0)
      setIsMsgStatus({
        status: true,
        type: "error",
        message: translate(TAKS_IMPORT, "selected_point_row_has_error", currentUser.language),
      });

    if (isAddToExistingMode) {
      setCheckedRows(prev => {
        const existingIds = new Set(prev.map(r => r.id));
        return [...prev, ...validRowIds.filter(r => !existingIds.has(r.id))];
      });
    } else {
      setCheckedRows(validRowIds);
    }
  };

  const handleBeforeChange = proceed => {
    if (groupStatusData.type === "list" || groupStatusData.ids.length === 0) {
      proceed();
      return;
    }

    if (wasAllPointsAdded) {
      const confirmed = window.confirm(
        translate(TAKS_IMPORT, "confirm_clear_all_points_before_change", currentUser.language)
      );
      if (confirmed) {
        setCheckedRows([]);
        setClearPointsTrigger(prev => prev + 1);
        setWasAllPointsAdded(false);
        proceed();
      }
    } else {
      const shouldAddToExisting = window.confirm(
        translate(TAKS_IMPORT, "confirm_reset_or_keep_points", currentUser.language)
      );
      if (shouldAddToExisting) {
        setForceUncheckedSelectAll(true);
      } else {
        setCheckedRows([]);
      }
      proceed();
    }
  };

  const handleGroupPopup = () => {
    setGroupPopup(prev => !prev);
  };

  const getPointGroupList = data => {
    if (data.length > 0) setList(data);
  };

  const checkedRowsIds = () => {
    return checkedRows.map(row => row.id);
  };

  const getFilterUrlKey = obj => {
    if (obj === null) {
      setFilterKeys();
      return;
    }
    setFilterKeys(prev => ({
      ...prev,
      [obj.key]: obj.value || "",
    }));
  };

  const openRow = row => {
    if (!groupPopup) history.push(`/point/${row.id}`);
  };

  return (
    <div className="container-fluid points-wrapper points-page">
      {isMsgStatus.status && <CreateMsgPopup setIsMsg={setIsMsgStatus} isMsg={isMsgStatus} />}
      <div
        className={`box p-4 points-wrapper-inner points-page-list ${
          groupStatusData.type === "edit" || groupStatusData.type === "new" ? "grouping-active" : ""
        }`}>
        {valid ? (
          <>
            {currentUser.role === "supervisor_manager" || currentUser.role === "supervisor" ? (
              <>
                <button className="grouping-btn" onClick={handleGroupPopup}>
                  <span className="material-symbols-outlined">workspaces</span>
                  {translate(TAKS_IMPORT, "grouping_btn_name", currentUser.language)}
                </button>
                <Grouping
                  isActive={groupPopup}
                  handleGroupPopup={handleGroupPopup}
                  checkedRowsIds={checkedRows}
                  getCurrentGroupData={getCurrentGroupData}
                  filterUrlKeys={filterKeys}
                  onAllPointsAdded={() => setWasAllPointsAdded(true)}
                  clearPointsTrigger={clearPointsTrigger}
                />
              </>
            ) : null}
            <EntityTable
              entity="point"
              dataPoints={getPointGroupList}
              getFilterUrlKey={getFilterUrlKey}
              openVisitFn={openRow}
              rowClassName="point-row-item"
              onBeforeChange={handleBeforeChange}>
              <TableHeaderColumn
                className="point-checkbox nnnn"
                dataField="checkbox"
                dataFormat={(cell, row) => (
                  <input className="point-checkbox" type="checkbox" checked={checkedRowsIds().includes(row.id)} onChange={e => handleCheckboxChange(e, row)} />
                )}
                width="50px">
                <input type="checkbox" checked={checkedRows.length > 0 && !forceUncheckedSelectAll} onChange={handleSelectAllOnePage} />
              </TableHeaderColumn>
              <TableHeaderColumn dataField="id" isKey hidden></TableHeaderColumn>
              <TableHeaderColumn dataFormat={networkName}>{translate(TAKS_IMPORT, "network_label", currentUser.language)}</TableHeaderColumn>
              <TableHeaderColumn dataField="name">{translate(TAKS_IMPORT, "name_title", currentUser.language)}</TableHeaderColumn>
              <TableHeaderColumn dataField="address">{translate(TAKS_IMPORT, "addresses_label", currentUser.language)}</TableHeaderColumn>
              <TableHeaderColumn dataField="code">{translate(TAKS_IMPORT, "code_label", currentUser.language)}</TableHeaderColumn>
              <TableHeaderColumn dataFormat={locality} dataField="locality">
                {translate(TAKS_IMPORT, "location_label", currentUser.language)}
              </TableHeaderColumn>
              <TableHeaderColumn dataFormat={pointState}>{translate(TAKS_IMPORT, "status", currentUser.language)}</TableHeaderColumn>
            </EntityTable>
          </>
        ) : (
          <div className="pt-page-not-found">
            <span className="material-symbols-outlined">location_off</span>
            <p>Page Not Found</p>
            <a href="/" className="ar-back-btn">
              {translate(TAKS_IMPORT, "back_btn", currentUser.language)}
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default withRouter(List);
